import { Alert, Button, Drawer, Menu, Modal, Select, Tooltip } from '@mantine/core';
import {
  IconBinaryTree2,
  IconFileExport,
  IconPlus,
  IconRefresh,
  IconRosetteDiscountCheck,
  IconSearch,
} from '@tabler/icons-react';
import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { useShell } from '../../shell-context.js';
import { vesselTime } from '../personnel/ship-clock.js';
import { errorCode } from '../personnel/source.js';
import type { PersonnelVessel } from '../personnel/types.js';
import { AuditTrailPanel } from '../pms/AuditTrailPanel.js';
import { utcToShipLocal } from '../pms/counter-form.js';
import type { VesselParticulars } from '../pms/types.js';
import { CertificateCalendar } from './CertificateCalendar.js';
import {
  AttachedDocuments,
  CertificateDetails,
  CertificateQuickLinks,
  type DetailTab,
  QuickActions,
  ReminderDialog,
} from './CertificateDetails.js';
import { CertificateDrawer, ConfirmRemove } from './CertificateDrawer.js';
import { CertificateListCard, type RowMenuItem } from './CertificateList.js';
import { CertificateVesselCard, DistributionPanel, UpcomingPanel } from './CertificateParts.js';
import { CertificateTree, useNodeLabel } from './CertificateTree.js';
import { CeAuthorizationDrawer, ClassFindingDrawer, SurveyDrawer } from './ClassDrawers.js';
import classes from './Compliance.module.css';
import {
  type CertificateSort,
  type CertificateSortKey,
  type CertificateView,
  certificateActions,
  certificateHistory,
  certificateTree,
  certificateTreeKey,
  certificateTreeRows,
  type DueFilter,
  defaultCertificateExpanded,
  exportCategory,
  filterCertificates,
  inScope,
  listScope,
  nodePath,
  ROOT_NODE,
  readCertificateQuery,
  type StatusFilter,
  scopeSummary,
  sortCertificates,
  typeIdsOf,
  upcomingRows,
  writeCertificateQuery,
} from './certificate-view.js';
import { useCertificateErrorText } from './errors.js';
import { ApproveIssuanceDialog, IssuanceApprovalBanner } from './IssuanceApproval.js';
import { AddRemarkDialog, ClassItemImportDialog } from './OfficeDialogs.js';
import { PageViewer } from './PageViewer.js';
import {
  type Loaded,
  RegulatoryRequirements,
  type RegulatoryTab,
} from './RegulatoryRequirements.js';
import { ConfirmationDialog, EndorsementDialog, IssuanceDialog } from './ShipDialogs.js';
import { SurveySchedule } from './SurveySchedule.js';
import type { ScanOwner, VesselCertificateSource } from './source.js';
import type {
  CeAuthorization,
  CertificateDetail,
  CertificateList,
  CertificatePage,
  CertificateType,
  ClassDueRow,
  ClassFinding,
  ClassItemList,
  EquipmentChoice,
  OnBoardIssuance,
  PersonChoice,
  SurveyKind,
  VesselSurvey,
} from './types.js';

export interface VesselCertificatesScreenProps {
  source: VesselCertificateSource;
  /** Saat (test ve ekran görüntüsü). */
  now?: () => Date;
}

const systemNow = () => new Date();
const DEFAULT_EXPIRING_DAYS = 30;

type Base =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | {
      kind: 'ready';
      vesselId: string;
      particulars: VesselParticulars | null;
      types: CertificateType[];
      list: CertificateList;
    };

type DetailState =
  | { kind: 'idle' }
  | { kind: 'loading'; id: string }
  | { kind: 'error'; id: string; code: string }
  | { kind: 'ready'; id: string; detail: CertificateDetail };

type Dialog =
  | { kind: 'certificate'; id: string | null }
  | { kind: 'remark'; id: string }
  | { kind: 'endorse'; id: string }
  | { kind: 'confirm' }
  | { kind: 'issue' }
  | { kind: 'survey'; survey: VesselSurvey | null; surveyKind: SurveyKind | null }
  | { kind: 'finding'; finding: ClassFinding | null }
  | { kind: 'authorization'; authorization: CeAuthorization | null }
  | { kind: 'import' }
  | { kind: 'reminder'; id: string }
  | { kind: 'remove'; id: string }
  | { kind: 'approve'; id: string }
  | { kind: 'audit'; id: string };

const EMPTY: Loaded<never> = { data: null, error: null };

/**
 * Compliance ▸ Vessel Certificates (`gemi-sertifika-takibi.jpeg`; plan M7 Task 27a, 27b): üst kart,
 * "Certificate Tracking" ağacı, Certificate List, Upcoming ve dağılım; sağda arama, eylemler,
 * Certificate Details, Attached Documents, Quick Actions ve Quick Links. Survey Schedule,
 * Regulatory Requirements ve Certificate Calendar sayfa içi görünümdür (menü değişmez). Ofis
 * sertifikayı yazar; gemi vize, sörveyör teyidi ve gemide düzenlenen sertifikayı yazar (K28).
 * Adres: web'de `?vessel=`, ağaç `?node=`, ayrıntı `?cert=`, görünüm `?view=`.
 */
export function VesselCertificatesScreen({
  source,
  now = systemNow,
}: VesselCertificatesScreenProps) {
  const { t, i18n } = useTranslation();
  const shell = useShell();
  const errorText = useCertificateErrorText();
  const nodeLabel = useNodeLabel();
  const locale = i18n.resolvedLanguage ?? 'en';
  const [localSearch, setLocalSearch] = useState('');
  const search = shell ? shell.search : localSearch;
  const query = readCertificateQuery(search);
  const setQuery = useCallback(
    (patch: Parameters<typeof writeCertificateQuery>[1]) => {
      const next = writeCertificateQuery(search, patch);
      if (next === (search || '')) return;
      if (shell) shell.navigate(`${shell.path}${next}`);
      else setLocalSearch(next);
    },
    [search, shell],
  );

  const [vessels, setVessels] = useState<PersonnelVessel[] | null>(null);
  const [vesselsError, setVesselsError] = useState<string | null>(null);
  const [base, setBase] = useState<Base>({ kind: 'loading' });
  const [detail, setDetail] = useState<DetailState>({ kind: 'idle' });
  const [reloads, setReloads] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [initialised, setInitialised] = useState<string | null>(null);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [focusRequest, setFocusRequest] = useState(0);
  const [treeDrawer, setTreeDrawer] = useState(false);
  const [typeId, setTypeId] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [due, setDue] = useState<DueFilter>('all');
  const [text, setText] = useState('');
  const [range, setRange] = useState<[string | null, string | null]>([null, null]);
  const [sort, setSort] = useState<CertificateSort>({ key: 'no', dir: 'asc' });
  const [page, setPage] = useState(1);
  const [detailTab, setDetailTab] = useState<DetailTab>('general');
  const [regTab, setRegTab] = useState<RegulatoryTab>('findings');
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [viewer, setViewer] = useState<{ index: number } | null>(null);
  const [notice, setNotice] = useState<{ tone: 'green' | 'yellow' | 'red'; text: string } | null>(
    null,
  );
  const [exporting, setExporting] = useState(false);
  /**
   * Kayıttan sonra açılacak sertifika: diyalog kapandıktan sonra adrese yazılır; yoksa kabuğun
   * koruması açık formu "kirli" sanıp onay sorar (Equipment List kalıbı).
   */
  const [pendingCert, setPendingCert] = useState<string | null>(null);
  const [surveys, setSurveys] = useState<Loaded<VesselSurvey[]>>(EMPTY);
  const [findings, setFindings] = useState<Loaded<ClassFinding[]>>(EMPTY);
  const [classItems, setClassItems] = useState<Loaded<ClassItemList>>(EMPTY);
  const [dueList, setDueList] = useState<Loaded<ClassDueRow[]>>(EMPTY);
  const [authorizations, setAuthorizations] = useState<Loaded<CeAuthorization[]>>(EMPTY);
  const [issuances, setIssuances] = useState<OnBoardIssuance[]>([]);
  const [equipment, setEquipment] = useState<EquipmentChoice[] | null>(null);
  const [people, setPeople] = useState<PersonChoice[] | null>(null);

  // Gemiler (web: filo; gemi: tek gemi)
  useEffect(() => {
    let live = true;
    source
      .vessels()
      .then((list) => {
        if (live) setVessels(list);
      })
      .catch((error: unknown) => {
        if (live) setVesselsError(errorCode(error));
      });
    return () => {
      live = false;
    };
  }, [source]);

  const vesselId = useMemo(() => {
    if (!vessels || vessels.length === 0) return null;
    if (source.scope === 'web' && query.vessel && vessels.some((v) => v.id === query.vessel)) {
      return query.vessel;
    }
    return vessels[0]?.id ?? null;
  }, [vessels, query.vessel, source.scope]);

  // Gemi değişince süzgeçler sıfırlanır (ağaç seçimi adreste düşer; `writeCertificateQuery`)
  // biome-ignore lint/correctness/useExhaustiveDependencies: yalnız gemi değişince
  useEffect(() => {
    setTypeId(null);
    setStatus('all');
    setDue('all');
    setText('');
    setRange([null, null]);
    setPage(1);
    setSurveys(EMPTY);
    setFindings(EMPTY);
    setClassItems(EMPTY);
    setDueList(EMPTY);
    setAuthorizations(EMPTY);
    setEquipment(null);
    setPeople(null);
  }, [vesselId]);

  // Liste, türler ve gemi bilgileri
  // biome-ignore lint/correctness/useExhaustiveDependencies: `reloads` yazmadan sonra yeniden okutur
  useEffect(() => {
    if (!vesselId) return;
    let live = true;
    setBase((current) =>
      current.kind === 'ready' && current.vesselId === vesselId ? current : { kind: 'loading' },
    );
    Promise.all([
      source.particulars(vesselId).catch(() => null),
      source.types(),
      source.list(vesselId),
    ])
      .then(([particulars, types, list]) => {
        if (live) setBase({ kind: 'ready', vesselId, particulars, types, list });
      })
      .catch((error: unknown) => {
        if (live) setBase({ kind: 'error', code: errorCode(error) });
      });
    return () => {
      live = false;
    };
  }, [source, vesselId, reloads]);

  const ready = base.kind === 'ready' && base.vesselId === vesselId ? base : null;
  const list = ready?.list ?? null;
  const types = ready?.types ?? [];
  const items = list?.items ?? [];
  const nodes = useMemo(
    () => (ready ? certificateTree(ready.types, ready.list.items) : []),
    [ready],
  );
  const nodeId = nodes.some((n) => n.id === query.node) ? (query.node as string) : ROOT_NODE;
  // Gemi saati kaydından (ADR 0065 §4)
  const timeZone = vesselTime(vessels?.find((v) => v.id === vesselId));
  const today = list?.today ?? utcToShipLocal(now(), timeZone).slice(0, 10);
  const expiringDays = list?.expiringDays ?? DEFAULT_EXPIRING_DAYS;
  const actions = certificateActions(source.scope, list?.can);

  // İlk açılışta (ve gemi değişince) referans ağacı açık
  useEffect(() => {
    if (!ready || initialised === ready.vesselId) return;
    setInitialised(ready.vesselId);
    setExpanded(defaultCertificateExpanded(nodes, nodeId));
  }, [ready, nodes, nodeId, initialised]);

  const typeIds = useMemo(() => typeIdsOf(nodes, nodeId), [nodes, nodeId]);
  /** Ağaçta tür seçiliyse yeni kaydın önerilen türü. */
  const selectedNode = nodes.find((n) => n.id === nodeId);
  const nodeTypeId = selectedNode?.kind === 'type' ? (selectedNode.typeIds[0] ?? null) : null;
  const scopeItems = useMemo(() => inScope(items, typeIds), [items, typeIds]);
  // Eski sertifikalar güncelin altında "History" (ADR 0116 §3)
  const history = useMemo(() => certificateHistory(items), [items]);
  const summary = useMemo(() => scopeSummary(scopeItems), [scopeItems]);
  const typeOrder = useMemo(() => {
    const order = new Map<string, number>();
    [...types]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .forEach((type, index) => {
        order.set(type.id, index);
      });
    return order;
  }, [types]);
  const filtered = useMemo(
    () =>
      sortCertificates(
        filterCertificates(items, { typeIds, typeId, status, due, query: text, range }),
        sort,
        typeOrder,
        locale,
      ),
    [items, typeIds, typeId, status, due, text, range, sort, typeOrder, locale],
  );
  const typeOptions = useMemo(
    () =>
      types
        .filter((type) => typeIds === null || typeIds.has(type.id))
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((type) => ({ value: type.id, label: type.name })),
    [types, typeIds],
  );
  const selectedId =
    query.cert && items.some((i) => i.id === query.cert) ? query.cert : (filtered[0]?.id ?? null);
  const selected = items.find((i) => i.id === selectedId) ?? null;

  // Ayrıntı (ofis sertifikası); gemide düzenlenen kayıt listedeki satırıyla gösterilir
  // biome-ignore lint/correctness/useExhaustiveDependencies: `reloads` yazmadan sonra yeniden okutur
  useEffect(() => {
    if (selected?.source !== 'office') {
      setDetail({ kind: 'idle' });
      return;
    }
    const id = selected.id;
    let live = true;
    setDetail((current) =>
      current.kind === 'ready' && current.id === id ? current : { kind: 'loading', id },
    );
    source
      .detail(id)
      .then((value) => {
        if (live) setDetail({ kind: 'ready', id, detail: value });
      })
      .catch((error: unknown) => {
        if (live) setDetail({ kind: 'error', id, code: errorCode(error) });
      });
    return () => {
      live = false;
    };
  }, [source, selected?.id, selected?.source, reloads]);

  const current = detail.kind === 'ready' && detail.id === selectedId ? detail.detail : null;

  // Görünümlerin verisi: yalnız açılınca (ve yazmadan sonra) okunur
  const loadInto = useCallback(
    <T,>(load: () => Promise<T>, set: (value: Loaded<T>) => void, keep: Loaded<T>) => {
      set({ data: keep.error ? null : keep.data, error: null });
      load()
        .then((data) => set({ data, error: null }))
        .catch((error: unknown) => set({ data: null, error: errorText(error) }));
    },
    [errorText],
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: görünüm, gemi ve yazma sayacı
  useEffect(() => {
    if (!vesselId) return;
    if (query.view === 'surveys') loadInto(() => source.surveys(vesselId), setSurveys, surveys);
    if (query.view === 'regulatory') {
      loadInto(() => source.findings(vesselId), setFindings, findings);
      loadInto(() => source.classItems(vesselId), setClassItems, classItems);
      loadInto(() => source.classDueList(vesselId), setDueList, dueList);
      loadInto(() => source.authorizations(vesselId), setAuthorizations, authorizations);
    }
  }, [source, vesselId, query.view, reloads]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: diyalog açılınca bir kez
  useEffect(() => {
    if (!vesselId || !dialog) return;
    if (dialog.kind === 'confirm' && dueList.data === null) {
      loadInto(() => source.classDueList(vesselId), setDueList, dueList);
    }
    if (dialog.kind === 'certificate') {
      source
        .issuances(vesselId)
        .then(setIssuances)
        .catch(() => setIssuances([]));
    }
    if (dialog.kind === 'finding' && equipment === null && source.office) {
      source.office
        .equipment(vesselId)
        .then(setEquipment)
        .catch(() => setEquipment([]));
    }
    if (dialog.kind === 'authorization' && people === null && source.office) {
      source.office
        .people(vesselId)
        .then(setPeople)
        .catch(() => setPeople([]));
    }
  }, [dialog?.kind, vesselId]);

  const reload = () => setReloads((n) => n + 1);
  // Diyalog kapanınca (çekmecenin kirli işareti düşmüşken) adrese yazılır
  useEffect(() => {
    if (pendingCert === null || dialog !== null) return;
    setPendingCert(null);
    setQuery({ cert: pendingCert });
  }, [pendingCert, dialog, setQuery]);
  const label = (node: (typeof nodes)[number]) => nodeLabel(node);
  const rows = useMemo(() => certificateTreeRows(nodes, expanded), [nodes, expanded]);
  const selectNode = (id: string) => {
    setFocusedId(id);
    setExpanded((open) => new Set([...open, ...nodePath(nodes, id).map((n) => n.id)]));
    setTypeId(null);
    setPage(1);
    setQuery({ node: id === ROOT_NODE ? null : id, cert: null });
    setTreeDrawer(false);
  };
  const toggle = (id: string) =>
    setExpanded((open) => {
      const next = new Set(open);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const onKey = (key: string, id: string) => {
    const action = certificateTreeKey(key, rows, id);
    if (!action) return;
    if (action.toggle) {
      const { id: target, open } = action.toggle;
      setExpanded((set) => {
        const next = new Set(set);
        if (open) next.add(target);
        else next.delete(target);
        return next;
      });
    }
    if (action.focus) {
      setFocusedId(action.focus);
      setFocusRequest((n) => n + 1);
    }
    if (action.select) selectNode(action.select);
  };
  const openCert = (id: string) => setQuery({ cert: id });
  const filterPage =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      setPage(1);
    };
  const onSort = (key: CertificateSortKey) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }));

  const pagesOf = (id: string | null): CertificatePage[] =>
    detail.kind === 'ready' && detail.id === id ? detail.detail.pages : [];
  const downloadPage = (pageItem: CertificatePage) => {
    source
      .downloadPage({ id: pageItem.id, fileName: pageItem.fileName })
      .catch((error: unknown) => setNotice({ tone: 'red', text: errorText(error) }));
  };
  const downloadAll = () => {
    for (const pageItem of [...pagesOf(selectedId)].sort((a, b) => a.sortOrder - b.sortOrder)) {
      if (pageItem.onBoard === undefined || pageItem.onBoard === 'on_board') downloadPage(pageItem);
    }
  };

  const exportList = async (format: 'xlsx' | 'csv') => {
    if (!source.exportReport || !vesselId) return;
    setExporting(true);
    try {
      const category = exportCategory(nodes, nodeId);
      await source.exportReport(vesselId, {
        format,
        lang: locale.startsWith('tr') ? 'tr' : 'en',
        ...(category ? { query: { category } } : {}),
      });
    } catch {
      setNotice({ tone: 'red', text: t('vesselCertificates.toolbar.exportFailed') });
    } finally {
      setExporting(false);
    }
  };

  /** Gemideki kaydın taraması: kayıt yazılır, ek düşerse ayrıca söylenir (K15 kalıbı). */
  const attach = async (owner: ScanOwner, id: string, scan: File | null) => {
    if (!scan || !source.ship) return;
    try {
      await source.ship.attachScan(owner, id, scan);
    } catch (error) {
      setNotice({
        tone: 'yellow',
        text: t('vesselCertificates.dialogs.scanFailed', { reason: errorText(error) }),
      });
    }
  };

  const rowMenu = (item: (typeof items)[number]): RowMenuItem[] => {
    const menu: RowMenuItem[] = [];
    const q = (key: string) => t(`vesselCertificates.quickActions.${key}`);
    if (item.pageCount > 0) {
      menu.push({
        key: 'document',
        label: q('viewDocument'),
        icon: 'document',
        onClick: () => {
          openCert(item.id);
          setViewer({ index: 0 });
        },
      });
    }
    // Gemide düzenlenen sertifikanın ofis onayı (ADR 0116 §2)
    if (actions.approve && item.officeEntryPending && source.office?.acceptIssuance) {
      menu.push({
        key: 'approve',
        label: q('approve'),
        icon: 'endorse',
        onClick: () => {
          openCert(item.id);
          setDialog({ kind: 'approve', id: item.id });
        },
      });
    }
    if (actions.remark && item.source === 'office') {
      menu.push({
        key: 'remark',
        label: q('addRemark'),
        icon: 'remark',
        onClick: () => {
          openCert(item.id);
          setDialog({ kind: 'remark', id: item.id });
        },
      });
    }
    if (actions.endorse && item.source === 'office' && !item.superseded) {
      menu.push({
        key: 'endorse',
        label: q('endorse'),
        icon: 'endorse',
        onClick: () => setDialog({ kind: 'endorse', id: item.id }),
      });
    }
    menu.push({
      key: 'reminder',
      label: q('reminder'),
      icon: 'reminder',
      onClick: () => setDialog({ kind: 'reminder', id: item.id }),
    });
    if (actions.remove && item.source === 'office') {
      menu.push({
        key: 'remove',
        label: t('vesselCertificates.dialogs.certificate.remove'),
        icon: 'remove',
        danger: true,
        onClick: () => setDialog({ kind: 'remove', id: item.id }),
      });
    }
    return menu;
  };

  const vesselName =
    ready?.particulars?.name ?? vessels?.find((v) => v.id === vesselId)?.name ?? '';
  const failed = vesselsError ?? (base.kind === 'error' ? base.code : null);
  const loading = !failed && (vessels === null || (vesselId !== null && !ready));
  const title = t('menu.complianceVesselCertificates');
  const crumbNode = nodes.find((n) => n.id === nodeId);

  if (vessels !== null && vessels.length === 0) {
    return (
      <div className={classes.screen}>
        <PageHeader title={title} />
        <Panel>
          <EmptyState title={t('vesselCertificates.noVessel')} />
        </Panel>
      </div>
    );
  }

  const primary = actions.add ? (
    <Button
      className={classes.toolbarAdd}
      leftSection={<IconPlus size={16} aria-hidden />}
      onClick={() => setDialog({ kind: 'certificate', id: null })}
    >
      {t('vesselCertificates.toolbar.add')}
    </Button>
  ) : actions.issue ? (
    <Button
      className={classes.toolbarAdd}
      leftSection={<IconRosetteDiscountCheck size={16} aria-hidden />}
      onClick={() => setDialog({ kind: 'issue' })}
    >
      {t('vesselCertificates.toolbar.issue')}
    </Button>
  ) : null;

  const exportButton = source.exportReport ? (
    <Menu position="bottom-end" withinPortal>
      <Menu.Target>
        <Tooltip label={t('vesselCertificates.toolbar.exportHint')} withinPortal>
          <Button
            variant="default"
            leftSection={<IconFileExport size={16} aria-hidden />}
            loading={exporting}
            disabled={!ready}
          >
            {t('vesselCertificates.toolbar.export')}
          </Button>
        </Tooltip>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item onClick={() => void exportList('xlsx')}>
          {t('pmsReports.export.excel')}
        </Menu.Item>
        <Menu.Item onClick={() => void exportList('csv')}>{t('pmsReports.export.csv')}</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  ) : (
    <Tooltip label={t('vesselCertificates.toolbar.exportHint')} withinPortal>
      <span className={classes.disabledWrap}>
        <Button variant="default" leftSection={<IconFileExport size={16} aria-hidden />} disabled>
          {t('vesselCertificates.toolbar.export')}
        </Button>
      </span>
    </Tooltip>
  );

  const treeView = (
    <CertificateTree
      rows={rows}
      selectedId={nodeId}
      focusedId={focusedId}
      onSelect={selectNode}
      onToggle={toggle}
      onKey={onKey}
      onFocus={setFocusedId}
      focusRequest={focusRequest}
    />
  );

  const back = () => setQuery({ view: 'list' });
  const office = source.office;
  let main: ReactNode;
  if (query.view === 'surveys') {
    main = (
      <SurveySchedule
        surveys={surveys.data}
        error={surveys.error}
        onRetry={reload}
        onBack={back}
        {...(actions.office
          ? {
              onAdd: (surveyKind: SurveyKind | null) =>
                setDialog({ kind: 'survey', survey: null, surveyKind }),
              onEdit: (survey: VesselSurvey) =>
                setDialog({ kind: 'survey', survey, surveyKind: survey.kind }),
            }
          : {})}
      />
    );
  } else if (query.view === 'regulatory') {
    main = (
      <RegulatoryRequirements
        tab={regTab}
        onTab={setRegTab}
        findings={findings}
        items={classItems}
        due={dueList}
        authorizations={authorizations}
        onRetry={reload}
        onBack={back}
        office={
          actions.office
            ? {
                addFinding: () => setDialog({ kind: 'finding', finding: null }),
                editFinding: (finding) => setDialog({ kind: 'finding', finding }),
                addAuthorization: () => setDialog({ kind: 'authorization', authorization: null }),
                editAuthorization: (authorization) =>
                  setDialog({ kind: 'authorization', authorization }),
                importItems: () => setDialog({ kind: 'import' }),
              }
            : undefined
        }
        onConfirm={actions.confirm ? () => setDialog({ kind: 'confirm' }) : undefined}
      />
    );
  } else if (query.view === 'calendar') {
    main = (
      <CertificateCalendar
        items={list ? scopeItems : null}
        today={today}
        onOpen={(id) => setQuery({ cert: id })}
        onBack={back}
      />
    );
  } else {
    main = (
      <CertificateListCard
        scope={listScope(nodes, nodeId, label)}
        items={list ? filtered : null}
        scopeCount={scopeItems.filter((i) => !i.superseded).length}
        totalCount={items.length}
        summary={list ? summary : null}
        expiringDays={expiringDays}
        typeOptions={typeOptions}
        typeId={typeId}
        status={status}
        due={due}
        query={text}
        onTypeId={filterPage(setTypeId)}
        onStatus={filterPage(setStatus)}
        onDue={filterPage(setDue)}
        onQuery={filterPage(setText)}
        sort={sort}
        onSort={onSort}
        page={page}
        onPage={setPage}
        selectedId={selectedId}
        onSelect={openCert}
        canEdit={actions.edit}
        scopeKind={source.scope}
        onEdit={(item) => {
          openCert(item.id);
          setDialog({ kind: 'certificate', id: item.id });
        }}
        rowMenu={rowMenu}
        history={status === 'superseded' ? undefined : history}
        defaultHistoryOpen={[...history]
          .filter(([, older]) => older.some((past) => past.id === query.cert))
          .map(([id]) => id)}
        emptyAction={
          actions.add ? (
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={() => setDialog({ kind: 'certificate', id: null })}
            >
              {t('vesselCertificates.toolbar.add')}
            </Button>
          ) : undefined
        }
      />
    );
  }

  const dialogItem =
    dialog && 'id' in dialog && dialog.id ? (items.find((i) => i.id === dialog.id) ?? null) : null;
  const editReady =
    dialog?.kind === 'certificate' && (dialog.id === null || (current && current.id === dialog.id));

  return (
    <div className={classes.screen} data-certificates-screen>
      <PageHeader title={title} titleHidden {...(crumbNode ? { crumb: label(crumbNode) } : {})} />
      {failed ? (
        <Panel>
          <EmptyState
            tone="danger"
            title={t('vesselCertificates.error')}
            description={errorText({ code: failed })}
            action={
              <Button
                size="xs"
                variant="light"
                leftSection={<IconRefresh size={14} />}
                onClick={() => {
                  setVesselsError(null);
                  setBase({ kind: 'loading' });
                  if (vessels === null) {
                    source
                      .vessels()
                      .then(setVessels)
                      .catch((error: unknown) => setVesselsError(errorCode(error)));
                  } else reload();
                }}
              >
                {t('vesselCertificates.retry')}
              </Button>
            }
          />
        </Panel>
      ) : (
        <>
          {notice ? (
            <Alert
              color={notice.tone}
              withCloseButton
              closeButtonLabel={t('vesselCertificates.dialogs.close')}
              onClose={() => setNotice(null)}
              role={notice.tone === 'red' ? 'alert' : 'status'}
            >
              {notice.text}
            </Alert>
          ) : null}
          <IssuanceApprovalBanner
            items={items}
            scope={source.scope}
            canApprove={actions.approve && Boolean(source.office?.acceptIssuance)}
            onOpen={(id) => setQuery({ cert: id, view: 'list' })}
            onApprove={(id) => {
              openCert(id);
              setDialog({ kind: 'approve', id });
            }}
          />
          <div className={classes.grid} aria-busy={loading}>
            <div className={classes.treeArea}>
              <Panel className={classes.treePanel}>{loading ? <TreeSkeleton /> : treeView}</Panel>
            </div>
            <div className={classes.topArea}>
              <CertificateVesselCard
                particulars={ready?.particulars ?? null}
                vessels={vessels ?? []}
                scope={source.scope}
                range={range}
                onRange={filterPage(setRange)}
                onVessel={(id) => setQuery({ vessel: id })}
                onOpenTree={() => setTreeDrawer(true)}
              />
            </div>
            <div className={classes.mainArea}>{main}</div>
            <div className={classes.bottomArea}>
              <UpcomingPanel
                rows={list ? upcomingRows({ ...list, items: scopeItems }, today) : null}
                onOpen={openCert}
                onViewAll={() => {
                  setDue('90');
                  setStatus('all');
                  setPage(1);
                  setQuery({ view: 'list' });
                }}
              />
              <DistributionPanel summary={list ? summary : null} />
            </div>
            <div className={classes.sideArea}>
              <div className={classes.toolbar}>
                <Select
                  className={classes.toolbarSearch}
                  aria-label={t('vesselCertificates.toolbar.searchLabel')}
                  placeholder={t('vesselCertificates.toolbar.search')}
                  leftSection={<IconSearch size={15} aria-hidden />}
                  searchable
                  clearable
                  value={null}
                  nothingFoundMessage={t('vesselCertificates.toolbar.noResults')}
                  data={items.map((item) => ({
                    value: item.id,
                    label: `${item.name} · ${item.number}`,
                  }))}
                  onChange={(value) => {
                    if (value) setQuery({ cert: value, view: 'list' });
                  }}
                  comboboxProps={{ withinPortal: true }}
                />
                {primary}
                {exportButton}
              </div>
              <div className={classes.sideCards}>
                <CertificateDetails
                  item={selected}
                  detail={current}
                  status={
                    loading
                      ? 'loading'
                      : detail.kind === 'loading'
                        ? 'loading'
                        : detail.kind === 'error'
                          ? 'error'
                          : 'ready'
                  }
                  error={detail.kind === 'error' ? errorText({ code: detail.code }) : undefined}
                  onRetry={reload}
                  vesselName={vesselName}
                  scope={source.scope}
                  tab={detailTab}
                  onTab={setDetailTab}
                  onViewPage={(index) => setViewer({ index })}
                  onAudit={
                    source.auditTrail && current
                      ? () => setDialog({ kind: 'audit', id: current.id })
                      : undefined
                  }
                  onApprove={
                    selected?.officeEntryPending && actions.approve && source.office?.acceptIssuance
                      ? () => setDialog({ kind: 'approve', id: selected.id })
                      : undefined
                  }
                />
                <AttachedDocuments
                  pages={
                    selected?.source === 'office'
                      ? current
                        ? current.pages
                        : detail.kind === 'error'
                          ? []
                          : null
                      : []
                  }
                  onView={(index) => setViewer({ index })}
                  onDownload={downloadPage}
                />
                <QuickActions
                  scope={source.scope}
                  hasPages={(current?.pages.length ?? 0) > 0}
                  canRemark={actions.remark && Boolean(current)}
                  canEndorse={actions.endorse && Boolean(current) && !current?.superseded}
                  onViewDocument={() => setViewer({ index: 0 })}
                  onDownload={downloadAll}
                  onRemark={() => selectedId && setDialog({ kind: 'remark', id: selectedId })}
                  onEndorse={() => selectedId && setDialog({ kind: 'endorse', id: selectedId })}
                  onReminder={() => selectedId && setDialog({ kind: 'reminder', id: selectedId })}
                />
                <CertificateQuickLinks
                  scope={source.scope}
                  view={query.view}
                  canAdd={actions.add}
                  canIssue={actions.issue}
                  onAdd={() => setDialog({ kind: 'certificate', id: null })}
                  onIssue={() => setDialog({ kind: 'issue' })}
                  onView={(view: CertificateView) => setQuery({ view })}
                />
              </div>
            </div>
          </div>
          {primary ? <div className={classes.mobileBar}>{mobilePrimary(primary)}</div> : null}
        </>
      )}

      <Drawer
        opened={treeDrawer}
        onClose={() => setTreeDrawer(false)}
        position="left"
        size={320}
        title={
          <span className={classes.drawerTitle}>
            <IconBinaryTree2 size={18} aria-hidden /> {t('vesselCertificates.tree.root')}
          </span>
        }
        closeButtonProps={{ 'aria-label': t('vesselCertificates.tree.close') }}
      >
        {treeDrawer ? treeView : null}
      </Drawer>

      <PageViewer
        opened={viewer !== null}
        certificate={selected}
        pages={pagesOf(selectedId)}
        index={viewer?.index ?? 0}
        onIndex={(index) => setViewer({ index })}
        onClose={() => setViewer(null)}
        pageUrl={(id) => source.pageUrl(id, 'preview')}
        onDownload={downloadPage}
        printer={source.printer}
        print={() => source.print()}
        now={now}
      />

      <ReminderDialog
        opened={dialog?.kind === 'reminder'}
        item={dialog?.kind === 'reminder' ? dialogItem : null}
        onClose={() => setDialog(null)}
      />

      {office && vesselId ? (
        <>
          <CertificateDrawer
            opened={Boolean(editReady)}
            detail={dialog?.kind === 'certificate' && dialog.id ? current : null}
            types={types}
            items={items}
            issuances={issuances}
            typeId={nodeTypeId}
            today={today}
            canRemove={actions.remove}
            onClose={() => setDialog(null)}
            onSave={async (input, existing) => {
              const saved = existing
                ? await office.update(existing.id, existing.rowVersion, input)
                : await office.create(vesselId, input);
              if (!existing) setPendingCert(saved.id);
              reload();
              return saved;
            }}
            onRemove={async (existing) => {
              await office.remove(existing.id, existing.rowVersion);
              setQuery({ cert: null });
              reload();
            }}
            pages={(certificateId) => ({
              upload: async (file, onProgress) => {
                const saved = await office.uploadPage(certificateId, file, onProgress);
                reload();
                return saved;
              },
              move: async (pageItem, sortOrder) => {
                await office.movePage(pageItem.id, pageItem.rowVersion, sortOrder);
                reload();
              },
              remove: async (pageItem) => {
                await office.removePage(pageItem.id, pageItem.rowVersion);
                reload();
              },
            })}
          />
          <ApproveIssuanceDialog
            opened={dialog?.kind === 'approve' && dialogItem !== null}
            item={dialog?.kind === 'approve' ? dialogItem : null}
            replaced={
              dialog?.kind === 'approve' && dialogItem?.previousId
                ? (items.find((i) => i.id === dialogItem.previousId) ?? null)
                : null
            }
            onClose={() => setDialog(null)}
            onApprove={async () => {
              if (!dialogItem || !vesselId || !office.acceptIssuance) return;
              const created = await office.acceptIssuance(vesselId, dialogItem.id);
              setDialog(null);
              setNotice({
                tone: 'green',
                text: t('vesselCertificates.approval.approved', { name: created.name }),
              });
              setPendingCert(created.id);
              reload();
            }}
          />
          <AddRemarkDialog
            opened={dialog?.kind === 'remark' && current?.id === dialog.id}
            certificate={dialogItem}
            onClose={() => setDialog(null)}
            onSubmit={async (remark) => {
              if (!current) return;
              await office.addRemark(current.id, current.rowVersion, remark);
              setDialog(null);
              setDetailTab('remarks');
              reload();
            }}
          />
          <ConfirmRemove
            opened={dialog?.kind === 'remove'}
            title={t('vesselCertificates.dialogs.certificate.removeTitle')}
            body={t('vesselCertificates.dialogs.certificate.removeBody', {
              name: dialogItem?.name ?? '',
            })}
            confirm={t('vesselCertificates.dialogs.certificate.remove')}
            onClose={() => setDialog(null)}
            onConfirm={async () => {
              if (!dialogItem || dialogItem.rowVersion === null) return;
              await office.remove(dialogItem.id, dialogItem.rowVersion);
              setDialog(null);
              if (selectedId === dialogItem.id) setQuery({ cert: null });
              reload();
            }}
          />
          <SurveyDrawer
            opened={dialog?.kind === 'survey'}
            survey={dialog?.kind === 'survey' ? dialog.survey : null}
            kind={dialog?.kind === 'survey' ? dialog.surveyKind : null}
            canRemove={actions.remove}
            onClose={() => setDialog(null)}
            onSave={async (input, existing) => {
              if (existing) await office.updateSurvey(existing.id, existing.rowVersion, input);
              else await office.createSurvey(vesselId, input);
              setDialog(null);
              reload();
            }}
            onRemove={async (existing) => {
              await office.removeSurvey(existing.id, existing.rowVersion);
              setDialog(null);
              reload();
            }}
          />
          <ClassFindingDrawer
            opened={dialog?.kind === 'finding'}
            finding={dialog?.kind === 'finding' ? dialog.finding : null}
            today={today}
            equipment={equipment}
            canRemove={actions.remove}
            onClose={() => setDialog(null)}
            onSave={async (input, existing) => {
              if (existing) await office.updateFinding(existing.id, existing.rowVersion, input);
              else await office.createFinding(vesselId, input);
              setDialog(null);
              reload();
            }}
            onRemove={async (existing) => {
              await office.removeFinding(existing.id, existing.rowVersion);
              setDialog(null);
              reload();
            }}
          />
          <CeAuthorizationDrawer
            opened={dialog?.kind === 'authorization'}
            authorization={dialog?.kind === 'authorization' ? dialog.authorization : null}
            people={people}
            canRemove={actions.remove}
            onClose={() => setDialog(null)}
            onSave={async (input, existing) => {
              if (existing) {
                await office.updateAuthorization(existing.id, existing.rowVersion, input);
              } else await office.createAuthorization(vesselId, input);
              setDialog(null);
              reload();
            }}
            onRemove={async (existing) => {
              await office.removeAuthorization(existing.id, existing.rowVersion);
              setDialog(null);
              reload();
            }}
          />
          <ClassItemImportDialog
            opened={dialog?.kind === 'import'}
            onClose={() => setDialog(null)}
            onImport={(file, apply) => office.importClassItems(vesselId, file, apply)}
            onApplied={reload}
          />
        </>
      ) : null}

      {source.ship ? (
        <>
          <EndorsementDialog
            opened={dialog?.kind === 'endorse'}
            certificate={dialog?.kind === 'endorse' ? dialogItem : null}
            today={today}
            onClose={() => setDialog(null)}
            onSubmit={async (input, scan) => {
              if (dialog?.kind !== 'endorse' || !source.ship) return;
              const saved = await source.ship.endorse(dialog.id, input);
              setDialog(null);
              setNotice(
                saved.warning
                  ? { tone: 'yellow', text: t('vesselCertificates.dialogs.endorsement.outside') }
                  : { tone: 'green', text: t('vesselCertificates.dialogs.endorsement.saved') },
              );
              await attach('certificate_endorsement', saved.id, scan);
              setDetailTab('history');
              reload();
            }}
          />
          <ConfirmationDialog
            opened={dialog?.kind === 'confirm'}
            rows={dueList.data ?? []}
            today={today}
            onClose={() => setDialog(null)}
            onSubmit={async (input, scan) => {
              if (!source.ship) return;
              const saved = await source.ship.confirm(input);
              setDialog(null);
              await attach('class_confirmation', saved.id, scan);
              reload();
            }}
          />
          <IssuanceDialog
            opened={dialog?.kind === 'issue'}
            types={types}
            items={items}
            typeId={nodeTypeId}
            today={today}
            onClose={() => setDialog(null)}
            onSubmit={async (input, scan) => {
              if (!source.ship) return;
              const saved = await source.ship.issueOnBoard(input);
              setDialog(null);
              await attach('certificate_issuance', saved.id, scan);
              setPendingCert(saved.id);
              reload();
            }}
          />
        </>
      ) : null}

      {source.auditTrail && vesselId ? (
        <Modal
          opened={dialog?.kind === 'audit'}
          onClose={() => setDialog(null)}
          size="xl"
          title={
            <span className={classes.drawerTitle}>
              {t('vesselCertificates.details.history.auditTitle', { name: selected?.name ?? '' })}
            </span>
          }
          closeButtonProps={{ 'aria-label': t('vesselCertificates.dialogs.close') }}
        >
          {dialog?.kind === 'audit' ? (
            <AuditTrailPanel
              load={(range) =>
                source.auditTrail?.(vesselId, {
                  table: 'vessel_certificates',
                  rowId: dialog.id,
                  ...range,
                }) ?? Promise.reject(new Error('NOT_FOUND'))
              }
            />
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
}

/** Dar ekranın sabit çubuğundaki birincil eylem: aynı düğme tam genişlikte. */
function mobilePrimary(button: ReactElement) {
  return <div style={{ display: 'grid' }}>{button}</div>;
}

function TreeSkeleton() {
  return (
    <div className={classes.skeletonRows} aria-hidden>
      {Array.from({ length: 14 }, (_, i) => i).map((row) => (
        <span
          key={row}
          style={{
            display: 'block',
            height: 12,
            borderRadius: 4,
            background: 'var(--felix-table-head-bg)',
            width: `${55 + ((row * 17) % 40)}%`,
          }}
        />
      ))}
    </div>
  );
}
